/**
 * AI 회의록(minutes-svc) API client.
 *
 * 8443 edge-server.mjs 가 /minutes/* → minutes-svc(:4103) 로 프록시한다
 * (architecture-ai-meeting-minutes.md §5). 따라서 baseURL 은 chat-server 의
 * /api/v1 가 아니라 /minutes/api/v1.
 *
 * Bare axios 강제 (onboarding.ts 선례와 동일 근거):
 *   - apiClient 의 401 자동 refresh interceptor 는 chat-server 전용. minutes-svc 의
 *     401(INVALID_TOKEN) 을 토큰 만료로 오인해 /auth/refresh 루프를 돌 수 있다.
 *   - 본 모듈은 apiClient 를 import 하지 않는다.
 *
 * Cookie 전송: withCredentials: true — wc_at 쿠키 자동 첨부 (svc 가 검증).
 */

// eslint-disable-next-line no-restricted-imports
import axios from 'axios';
import type { VoiceSessionItem } from './voice-sessions';

const baseURL = import.meta.env.VITE_MINUTES_BASE_URL ?? '/minutes/api/v1';

/**
 * ★ `export` 는 2026-09-22 에 붙었다(동작은 안 바뀐다) — 「보내기·보낸 이력」 모듈이 **같은 client**
 *   를 써야 하기 때문이다. 따로 만들면 baseURL·withCredentials·timeout 이 **두 벌**이 되고,
 *   `VITE_MINUTES_BASE_URL` 을 바꾼 날 한쪽만 따라간다.
 */
export const minutesClient = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 90_000, // AI 생성 동기 — minutes_generate_ms(기본 60s) + 여유
});

export interface ActionItem {
  text: string;
  assignee?: string;
  due?: string;
}

export type MinutesStatus = 'pending' | 'ready' | 'failed';

/**
 * 이 회의록이 **어디서 왔나** — ⛔ 서버와 **같은 글자**다(`minutes.shape.ts:26` 의 `MINUTES_ORIGINS`).
 *
 * ```
 * minutes     사람이 원문을 붙여넣어 만든 것 — `source_text` 가 «있다»
 * voice_memo  녹음에서 만들어진 것         — `source_text` 가 «없다`(NULL) · `model` 도 null
 * ```
 * ★ 둘은 **가진 것이 다르다.** 그래서 목록·상세가 그 차이를 그려야 한다(「왜 이건 원문이 없나」).
 */
export const MINUTES_ORIGINS = ['minutes', 'voice_memo'] as const;
export type MinutesOrigin = (typeof MINUTES_ORIGINS)[number];

/**
 * ⭐ 2026-10-20 (사용자 결정 「나」) — 회의록 응답에 실린 «그 녹음의 숫자»(minutes-svc `voice`).
 * ★ 칸은 녹음 목록 줄(`VoiceSessionItem`)에서 «골라» 만든다 — 이름이 갈리면 tsc 가 잡는다
 *   (서버 창구 `voice-internal-counts.routes.ts` 도 녹음 목록과 글자 그대로 맞춘다).
 * ⚠ 녹음 목록에서는 «선택»인 셋(pending · transcript_chars · gave_up)이 여기서는 «늘» 있다 — 서버가 꼴이 어긋난 줄은 안 붙인다.
 * ⛔ 서버는 판정하지 않는다 — 「녹음된 내역 없음」은 `detailVoiceIsEmpty`(awaiting-summary.ts)가 가른다.
 */
export type MinutesVoiceCounts =
  Pick<VoiceSessionItem, 'status' | 'chunk_count' | 'received_chunks' | 'transcribed_chunks' | 'failed_chunks'>
  & Required<Pick<VoiceSessionItem, 'pending_chunks' | 'transcript_chars' | 'gave_up_chunks'>>;

// 목록 경량 행 (본문 제외)
export interface MinutesListItem {
  id: string;
  institution_id: string;
  owner_user_id: string;
  title: string;
  meeting_date: string | null;
  attendees: string[];
  /** ⚠ 음성에서 온 회의록은 **null** 이다 — 서버가 「확인 안 된 값」을 안 적는다. */
  model: string | null;
  status: MinutesStatus;
  /**
   * ★ 어디서 왔나 — 목록 응답에도 **있다**(`MinutesRow.origin`).
   * ⚠ 옛 서버는 이 칸을 안 주므로 `undefined` 일 수 있다 → 그때는 「손으로 쓴 것」으로 다룬다.
   */
  origin?: MinutesOrigin;
  created_at: string;
  updated_at: string;
  /**
   * ⭐ 2026-10-20 (「나」) — 이 회의록을 만든 녹음의 숫자. ⛔ 없으면(옛 서버 · 손으로 쓴 회의록 · 서버가 못 받음) 「모른다」 —
   *   그때는 종전처럼 녹음 목록(100개)에서 찾은 줄로 가른다.
   */
  voice?: MinutesVoiceCounts;
}

// 상세 — 본문 포함
export interface MinutesDetail extends MinutesListItem {
  /**
   * ★★★ **null 일 수 있다** (2026-09-22 정정 — 마이그 0005 가 `NOT NULL` 을 풀었다).
   *
   * ⛔ 「비어 있다」가 아니라 **「여기 있는 것이 아니다」**다(그 마이그 주석의 말). 음성에서 온
   *   회의록의 재료는 **녹음 조각**이고 회의록에 실려 오는 것은 **결과**다.
   * ⚠ 한때 이 타입이 `string` 이었다 — **서버 타입도 아직 `string` 이다**(`minutes.shape.ts:134`).
   *   그래서 화면이 「원문 보기」를 그리고 **빈 상자**를 보여 줬다. 타입이 거짓이면 화면이 거짓말을 한다.
   */
  source_text: string | null;
  summary: string | null;
  decisions: string[];
  action_items: ActionItem[];
  /**
   * ★★★ 이 회의록을 만든 ⭐ **녹음 세션** — ⇒ ⛔ 「직접 작성」이면 `null`/없음이다.
   *
   * ## ⚠ 서버는 ⭐ 2026-09-30 부터 ★ 내려 주고 있었다 — ⇒ ⛔ «웹 타입»만 몰랐다
   *
   * `apps/minutes-svc/src/repo/minutes.repo.ts:95` 의 `FULL_COLS` 에 이 칸이 있고,
   * 그 파일 머리말이 적고 있다 — 「✅ 2026-09-30 — 이 미룸은 «닫혔다».
   * `voice_session_id` 를 상세에 내린다」.
   * ⇒ ⚠ 그래서 ★ 「되짚어야 한다」가 ⭐ 아니다 — 상세 응답에 ★ 그대로 들어 있다.
   *
   * ## ⛔ 목록(`LIST_COLS`)에는 ★ 이 칸이 ⭐ 없다 — ⇒ 그래서 `MinutesDetail` 에만 둔다
   *
   * ⇒ ★ 「목록에 없으니 상세에도 없다」가 ⭐ 거짓이었다. ⇒ ⚠ 목록 줄에서 이것을 쓰려 하면
   *   `undefined` 를 받고 ★ 조용히 ⭐ 「직접 작성」으로 읽힌다.
   *
   * ## ⭐ 이 칸이 ★ 「요약하기」의 ⭐ 길을 가른다 (2026-10-09 · 관제 답 ⓐ)
   *
   *     있음  ⇒ POST /voice/sessions/:id/summarize   ★ 비동기 · 끝나면 알림
   *     없음  ⇒ ⛔ 웹에는 ★ 길이 «없다» — 부르는 파일 ⭐ 0 (앱은 regenerate 를 쓴다)
   *
   * ⇒ ⚠ 그래서 ★ 직접 쓴 회의록은 ⭐ 「요약이 없습니다.」까지만 말한다.
   *   ⇒ ★ 그 «없음»은 ⭐ 사용자 결정 ⑪ 로 올라가 있다(관제).
   */
  voice_session_id?: string | null;
}

export interface ListResult {
  items: MinutesListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateMinutesPayload {
  title: string;
  meeting_date?: string;
  attendees?: string[];
  source_text: string;
}

/**
 * AI 정리 실패(502 AI_GENERATION_FAILED) — minutes-svc 는 이 경우에도 원문을
 * status='failed' 로 보존한 행을 body.minutes 로 돌려준다(모바일과 동일 계약).
 * 보존된 행 + 사유를 실어, 호출부가 "저장된 항목 보기 / 재시도" UX(모바일 파리티)를
 * 제공할 수 있게 한다. 그 외 에러는 종전과 동일하게 그대로 throw.
 */
export class MinutesAiFailedError extends Error {
  readonly minutes: MinutesDetail | null;
  readonly reason: string;
  constructor(reason: string, minutes: MinutesDetail | null) {
    super(reason);
    this.name = 'MinutesAiFailedError';
    this.reason = reason;
    this.minutes = minutes;
  }
}

/**
 * 삭제 권한 없음(403 FORBIDDEN) — 회의록은 institution 공유 열람이나 삭제는 작성자(owner)만.
 * 비소유자는 UI 에서 삭제 버튼을 숨기지만, 만일의 경쟁(목록 stale 등)에 대비한 안전망으로
 * 호출부가 친절 안내를 띄울 수 있게 전용 에러로 변환한다(모바일 파리티).
 */
export class MinutesForbiddenError extends Error {
  constructor(message = 'forbidden') {
    super(message);
    this.name = 'MinutesForbiddenError';
  }
}

/**
 * ★★★ **`origin` 을 «반드시» 보낸다** (2026-09-22 — 안 보내서 음성 회의록이 «한 줄도» 안 보였다).
 *
 * ## ⛔ 서버 기본값은 «좁은 쪽»이고, 그것은 «일부러»다
 *
 * `origin` 을 안 주면 서버는 `['minutes']` 만 준다(`minutes.shape.ts` 의 `DEFAULT_ORIGINS`).
 * 그 파일 주석이 까닭을 적어 뒀다 — 「옛 앱은 이 칸을 모르므로 안 보낸다. **기본값이 그 앱의
 * 화면을 정한다.** 기본을 좁은 쪽으로 두면 모르는 앱은 종전과 똑같이 보인다」.
 * ⇒ **서버 결함이 아니라 「고르는 쪽」의 몫**이고, 8443 이 안 골라서 안 보였다.
 *
 * ## ⚠ 운영에서 실제로 난 일 (2026-09-22 22:27)
 *
 * 음성 한 건이 끝까지 돌아 `origin='voice_memo' · status='ready'` 로 **회의록이 만들어졌는데
 * 화면에서 못 봤다.** `?origin=voice_memo` 로 따로 불러야 보였다.
 *
 * ## ★ 모양은 **CSV** 다 — 배열로 보내면 400 이다
 *
 * `csvOf` 가 `a,b` 를 쪼개 **조각마다 enum 으로 다시** 본다(`minutes.shape.ts:99-106`).
 * 그래서 `?origin=minutes,voice_memo` 이고, 모르는 글자가 섞이면 **400** 이다.
 * ⚠ axios 에 배열을 주면 `origin[]=…` 이나 `origin=a&origin=b` 로 나가 그 검사에 걸린다 —
 *   그래서 **문자열로 만들어 넣는다.**
 *
 * ## ★ 옛 서버(이 칸을 모르는 판)에 보내도 안전하다
 *
 * `listQuery` 는 `z.object` 이고 strict 가 아니라 **모르는 칸을 조용히 버린다** — 400 이 아니다.
 * 그때는 origin 컬럼도 없어 종전처럼 전부 온다(그 시절엔 voice_memo 행 자체가 없었다).
 */
export async function listMinutes(params?: {
  limit?: number;
  offset?: number;
  status?: MinutesStatus;
  /**
   * 기본은 **둘 다**다 — 화면 이름이 「AI 회의록」인데 **AI 가 만든 것이 안 보이면** 그것이 결함이다.
   * ⛔ 좁히고 싶은 자리가 생기면 그때 명시해서 부른다(기본을 좁히지 않는다).
   */
  origin?: readonly MinutesOrigin[];
}): Promise<ListResult> {
  const { origin, ...rest } = params ?? {};
  const r = await minutesClient.get<ListResult>('/minutes', {
    params: { ...rest, origin: [...(origin ?? MINUTES_ORIGINS)].join(',') },
  });
  // 방어 가드(additive) — items 가 배열이 아닌 비정형 응답에도 소비처 map 크래시 차단.
  return { ...r.data, items: Array.isArray(r.data?.items) ? r.data.items : [] };
}

export async function getMinutes(id: string): Promise<MinutesDetail> {
  const r = await minutesClient.get<MinutesDetail>(`/minutes/${id}`);
  return r.data;
}

export async function createMinutes(payload: CreateMinutesPayload): Promise<MinutesDetail> {
  try {
    const r = await minutesClient.post<MinutesDetail>('/minutes', payload);
    return r.data;
  } catch (e) {
    // 502 AI_GENERATION_FAILED — 서버가 원문을 보존한 실패 행을 body.minutes 로 반환.
    // 전용 에러로 변환해 호출부가 실패 항목 보기/재시도 UX 를 띄울 수 있게 한다.
    if (axios.isAxiosError(e) && e.response?.status === 502) {
      const data = e.response.data as { error?: string; message?: string; minutes?: MinutesDetail } | undefined;
      if (data?.error === 'AI_GENERATION_FAILED') {
        throw new MinutesAiFailedError(data.message ?? 'AI generation failed', data.minutes ?? null);
      }
    }
    throw e;
  }
}

export async function deleteMinutes(id: string): Promise<void> {
  try {
    await minutesClient.delete(`/minutes/${id}`);
  } catch (e) {
    // 403 FORBIDDEN — 작성자(owner)만 삭제 가능. 전용 에러로 변환해 친절 안내 유도.
    if (axios.isAxiosError(e) && e.response?.status === 403) {
      throw new MinutesForbiddenError();
    }
    throw e;
  }
}
